import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeAlert,
  getNodeCameras,
  getNodeUsers,
  getNodeModelEventImage,
  getNodeAlertActions,
  addNodeAlertComment,
  acknowledgeNodeAlert,
  unacknowledgeNodeAlert,
  assignNodeAlert,
  resolveNodeAlert,
  cancelNodeAlert,
} from '../services/api.service';
import { tableToModel } from '../api/hexAlerts.core';
import { useCameraMedia } from '../hooks/useCameraMedia';
import { ActionModal, AssignModal, alertName } from './AlertsPage';
import {
  AlertRecord,
  AlertRecordHeader,
  PageTitle,
  snapshotTypeLabel,
  snapshotTypeRank,
} from '@trinetra/shared';

/**
 * AlertDetailView — one alert, everything about it: the frame (streamed from the node), the
 * clip around it or the camera live, why it fired, its lifecycle, activity and actions.
 *
 * The layout is the shared AlertRecord; this component only fetches over the site's tunnel and
 * runs the lifecycle calls. The SAME component serves the routed `/alerts/:id` page below AND
 * every modal that opens an alert (list, boards, dashboard), so they cannot drift apart.
 */
export function AlertDetailView({
  alertId,
  className = '',
  showTitle = true,
  titleActions = null,
  headerActions = null,
  onClose,
  nodeId: nodeIdProp,
}: {
  alertId: string;
  className?: string;
  /** The routed page puts the alert's name in the app's title bar; a modal does not. */
  showTitle?: boolean;
  /** Extra buttons beside the title in the app's title bar (the routed page's Back). */
  titleActions?: ReactNode;
  /** In a modal: the pager and "Full page", drawn in the record's own header. */
  headerActions?: ReactNode;
  /** In a modal: the record's close button. */
  onClose?: () => void;
  /**
   * Which site this alert belongs to. Defaults to the selected one, which is right everywhere
   * a site is selected — and wrong on the all-sites alert board, where NO site is selected and
   * the alert being opened may come from any of them. That board knows the answer (its camera
   * ids are site-qualified) and passes it, so the same detail view serves both.
   */
  nodeId?: string;
}) {
  const { selectedNode } = useNode();
  const { user, hasPermission } = useAuth();
  const canManageAlerts = hasPermission('alerts.edit'); // backend also enforces
  const nodeId = nodeIdProp || selectedNode?.nodeId || '';
  const uid = user?.id || null;
  const uname = user?.name || user?.email || null;

  const [alert, setAlert] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [cameraMap, setCameraMap] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<any[]>([]);
  const [actionKind, setActionKind] = useState<'resolve' | 'cancel' | null>(null);
  const [actionError, setActionError] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [snapshots, setSnapshots] = useState<{ type: string; url: string }[]>([]);
  const [snapsLoading, setSnapsLoading] = useState(false);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.full_name || u.name || u.email || u.id])), [users]);

  const load = useCallback(async () => {
    if (!nodeId || !alertId) { setLoading(false); return; }
    setLoading(true);
    try {
      setAlert(await getNodeAlert(nodeId, alertId));
    } catch (e) {
      console.error('Failed to load alert:', e);
      setAlert(null);
    } finally {
      setLoading(false);
    }
  }, [nodeId, alertId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId)
      .then((cams: any) => {
        const arr = Array.isArray(cams) ? cams : cams?.data || [];
        setCameraMap(Object.fromEntries(arr.map((c: any) => [c.id, c.name])));
      })
      .catch(() => {});
    getNodeUsers(nodeId)
      .then((list: any[]) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => setUsers([]));
  }, [nodeId]);

  // Fetch the alert's snapshot images through the tunnel image proxy (same mechanism the
  // hexagon preview uses). Node image_urls aren't directly reachable from the cloud, so each
  // image is streamed as base64→blob via getNodeModelEventImage(model, event_id, imageType).
  useEffect(() => {
    const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
    // Fetch order follows display order, so the frame shown first is also the first one
    // streamed through the tunnel.
    const types = Object.keys(urls).sort((a, b) => snapshotTypeRank(a) - snapshotTypeRank(b));
    const model = tableToModel(alert?.source_table);
    if (!alert || !nodeId || !alert.event_id || !model || types.length === 0) {
      setSnapshots([]);
      setSnapsLoading(false);
      return;
    }
    let cancelled = false;
    const made: string[] = [];
    setSnapshots([]);
    setSnapsLoading(true);
    (async () => {
      for (const type of types) {
        try {
          const blob: any = await getNodeModelEventImage(nodeId, model, alert.event_id, type);
          if (cancelled) return;
          // A tunnel 404/timeout still resolves as a (JSON/text) Blob — only keep real images.
          if (blob && (!blob.type || blob.type.startsWith('image/'))) {
            const url = URL.createObjectURL(blob);
            made.push(url);
            // Published one at a time: the wide shot appears while the crops are still in
            // flight. Collecting the lot and setting it at the end meant the record sat on
            // "Loading snapshots…" for three or four tunnel round trips — and one frame that
            // hung or 404'd hid the frames that had already arrived.
            setSnapshots((prev) => [...prev, { type, url }]);
            setSnapsLoading(false);
          }
        } catch { /* skip this image */ }
      }
      if (cancelled) { made.forEach((u) => URL.revokeObjectURL(u)); return; }
      setSnapsLoading(false);
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); };
    // Re-stream only when the alert's EVENT changes — an ack or a comment returns a new alert
    // row with the same frames, and refetching every image through the tunnel for it is waste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.id, alert?.event_id, alert?.source_table, nodeId]);

  // Every lifecycle call returns the updated alert row — apply it so the status and
  // the remaining actions update immediately; refetch if the shape is unexpected,
  // and surface the failure instead of only logging it.
  const runAction = async (fn: () => Promise<any>) => {
    setActionError('');
    try {
      const updated = await fn();
      if (updated && updated.id) setAlert(updated); else await load();
    } catch (e: any) {
      console.error(e);
      setActionError(e?.response?.data?.message || e?.message || 'Action failed');
    }
  };

  const doAck = () => runAction(() => acknowledgeNodeAlert(nodeId, alertId, uid, uname));
  const doUnack = () => runAction(() => unacknowledgeNodeAlert(nodeId, alertId, uid, uname));
  const doAssign = (assigneeId: string, assigneeName: string) => runAction(() => assignNodeAlert(nodeId, alertId, assigneeId, uid, assigneeName, uname));
  const doResolve = (note: string) => runAction(() => resolveNodeAlert(nodeId, alertId, uid, note, uname));
  const doCancel = (reason: string) => runAction(() => cancelNodeAlert(nodeId, alertId, uid, reason, uname));

  const cameraId: string | undefined = alert?.camera_id;
  const cameraName = cameraId ? cameraMap[cameraId] || cameraId : undefined;
  const { playback, renderLive } = useCameraMedia(nodeId, cameraId, cameraName);

  const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
  const hasImages = Object.values(urls).some(Boolean);

  const title = showTitle && (
    <PageTitle title={alert ? alertName(alert) : 'Alert'}>
      <button
        onClick={load}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
        title="Refresh"
      >
        <ArrowPathIcon className="h-4 w-4" />
      </button>
      {titleActions}
    </PageTitle>
  );

  if (loading || !alert) {
    return (
      <div className={`flex min-h-full flex-col ${className}`}>
        {title}
        {/* The header still renders while loading, so the modal's pager and close work. */}
        <AlertRecordHeader alert={null} actions={headerActions} onClose={onClose} />
        {loading ? (
          <div className="grid gap-6 px-6 py-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
            <div className="h-[40vh] animate-pulse rounded-xl bg-muted" />
            <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}</div>
          </div>
        ) : (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Alert not found{!nodeId ? ' — select a site from the sidebar.' : '.'}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {title}
      <AlertRecord
        className={className}
        alert={alert}
        cameraName={cameraName}
        userMap={userMap}
        images={snapshots.map((s) => ({ url: s.url, label: snapshotTypeLabel(s.type), key: s.type }))}
        imagesNote={hasImages
          ? snapsLoading ? 'Loading snapshots…' : 'Snapshots unavailable (site unreachable).'
          : 'No snapshots for this alert'}
        playback={playback}
        renderLive={renderLive}
        headerActions={headerActions}
        onClose={onClose}
        canManage={canManageAlerts}
        shortcuts={!assigning && !actionKind}
        error={actionError}
        currentUserName={uname}
        loadActions={() => getNodeAlertActions(nodeId, alert.id)}
        addComment={(text) => addNodeAlertComment(nodeId, alert.id, uid, uname, text)}
        onAck={doAck}
        onReopen={doUnack}
        onAssign={() => setAssigning(true)}
        onResolve={() => setActionKind('resolve')}
        onCancel={() => setActionKind('cancel')}
      />

      {/* Outside the record: these are their own overlays, not part of its layout. */}
      {assigning && (
        <AssignModal
          users={users}
          currentUser={{ id: uid, name: uname }}
          onClose={() => setAssigning(false)}
          onConfirm={(assigneeId: string, assigneeName: string) => { doAssign(assigneeId, assigneeName); setAssigning(false); }}
        />
      )}
      {actionKind && (
        <ActionModal
          kind={actionKind}
          onClose={() => setActionKind(null)}
          onConfirm={(value: string) => {
            if (actionKind === 'resolve') doResolve(value); else doCancel(value);
            setActionKind(null);
          }}
        />
      )}
    </>
  );
}

/**
 * AlertDetailPage — a routed single-alert view (/alerts/:id), reached from the alerts list and
 * from anywhere that deep-links an alert. The hexagon board opens AlertDetailView in a modal
 * instead, so the operator keeps the board.
 */
export default function AlertDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <div>
      <AlertDetailView
        alertId={id}
        className="rounded-xl border border-border bg-card shadow-card overflow-hidden"
        titleActions={(
          <button
            onClick={() => navigate('/alerts')}
            className="flex h-8 items-center gap-1 rounded-lg border border-border px-2.5 text-[12px] text-foreground hover:bg-muted transition-colors"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" /> Alerts
          </button>
        )}
      />
    </div>
  );
}
